/*
 * Seaber styled-system theme
 * see spec: https://styled-system.com/theme-specification
 */

export type Theme = typeof theme;

export const baseColors = {
  // Color scales
  neutral: {
    white_50: '#FFFFFF',
    white_100: '#f2f4f6',

    gray_50: '#e7e7e7',
    gray_100: '#e2e4e7',
    gray_150: '#D6DCE4',
    gray_155: '#d5dce5',
    gray_160: '#d3d5d8',
    gray_175: '#d2d2d2',
    gray_200: '#c6cdd8',
    gray_225: '#c4cdda',
    gray_250: '#8897AE',
    gray_300: '#757e8b',
    gray_340: '#657C92',
    gray_350: '#616b72',
    gray_390: '#4F5E71',
    gray_400: '#474a4e',
    gray_430: '#3A4151',
    gray_450: '#353738',
    gray_500: '#34373b',
    gray_600: '#313233',
    gray_700: '#2B3443',

    black_500: '#1c1c1c',
    black_600: '#191b1d',
    black_700: '#0D1721',
    black_900: '#000000',
  },
  blue: {
    blue_200: '#4F88E7',
    blue_300: '#3555A0',
    blue_400: '#3077D1',
    blue_500: '#182b80',
    blue_600: '#16233c',
  },
  orange: {
    orange_500: '#F09035',
    orange_600: '#e74c03',
    orange_700: '#D65726',
  },
  yellow: {
    yellow_300: '#ffe552',
  },
};

const { neutral, orange, yellow } = baseColors;

const theme = {
  colors: {
    ...baseColors,
    background: neutral.white_50,
    text: neutral.black_900,
    // Component specific colors
    status: {
      error: '#FC4A15',
      success: '#5FAD09',
    },
    form: {
      background: neutral.white_50,
      mutedBackground: neutral.white_100,
      border: neutral.gray_200,
    },
    navbar: {
      background: neutral.black_500,
      borderBottom: neutral.gray_600,
    },
    filterbar: {
      background: neutral.black_500,
    },
    dropdownList: {
      background: neutral.white_100,
      borderColor: neutral.gray_400,
    },
    input: {
      background: neutral.white_50,
      text: neutral.black_900,
      border: neutral.gray_300,
      placeholder: neutral.black_900,
      addonBackground: '#e7e7e7',
      addonText: '#6d6d6d',
      disabledBackground: '#f2f2f2',
    },
    orderStatus: {
      assigned: '#426ABA',
      otherUpdated: '#182b80',
      committed: '#616b72',
      unassigned: '#ec772f',
      shared: '#191b1d',
      suggested: '#07bc0c',
    },
    contractStatus: {
      inquiry: yellow.yellow_300,
      onSubs: orange.orange_500,
      subsExpired: orange.orange_600,
    },
    voyageEvents: {
      loading: '#5fad09',
      discharge: '#34373b',
      voyage: '#9ba2ad',
      idle: '#FC4A15',
      violation: '#FC2815',
      event: '#1FBBFF',
      eventWindow: '#2D3D55',
      portCall: '#60DDFF',
    },
    time: {
      actual: '#5fad09',
    },
  },
  space: [0, 2, 4, 8, 16, 32, 64],
  fonts: {
    body: '"Inter", "Space Grotesk", "Helvetica", fallback-font, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Ubuntu, "Helvetica Neue", sans-serif',
    heading:
      '"Inter", "Space Grotesk", "Helvetica", fallback-font, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Ubuntu, "Helvetica Neue", sans-serif',
  },
  fontSizes: [
    '0.625rem',
    '0.75rem',
    '0.875rem',
    '1rem',
    '1.25rem',
    '1.5rem',
    '2rem',
  ],
  // Components with style variants
  button: {
    default: {
      background: neutral.gray_400,
      text: neutral.white_50,
      border: '#696c6f',
      hover: {
        background: neutral.black_500,
      },
      active: {
        background: '#ffffff1f',
      },
    },
    primary: {
      background: yellow.yellow_300,
      text: neutral.black_900,
      border: '#91a9ca80',
      hover: {
        background: '#ffdd1f',
      },
      active: {
        background: '#ebc800',
      },
    },
    secondary: {
      background: neutral.white_100,
      text: neutral.black_900,
      border: neutral.gray_225,
      hover: {
        background: neutral.gray_150,
      },
      active: {
        background: neutral.white_50,
      },
    },
    transparent: {
      background: 'transparent',
      text: neutral.black_700,
      border: neutral.gray_250,
      hover: {
        background: neutral.gray_150,
      },
      active: {
        background: neutral.white_50,
      },
    },
    positive: {
      background: '#5fad09',
      text: neutral.white_50,
      border: '#5fad09',
    },
    negative: {
      background: orange.orange_600,
      text: neutral.white_50,
      border: orange.orange_500,
    },
    dark: {
      background: neutral.gray_450,
      text: neutral.white_50,
      border: '#5b5d5e',
      hover: {
        background: neutral.black_500,
      },
    },
  },
};

export default theme;
